/**
 * Shared signup pages (Foodbuy, HSSI) — shipping address Google Places autocomplete
 *
 * company-register-v2.js binds Places autocomplete to the billing #street_1
 * only. This file adds the same behaviour to the "different shipping address"
 * Street Address (#shipping_street_1): picking a suggestion fills street,
 * city, state (+ state code), zip and country.
 *
 * Load order is not guaranteed (Maps loads asynchronously), so initialisation
 * is triggered from whichever happens first: the wrapped global
 * `initAutocomplete` Maps callback, or DOMContentLoaded when Maps is already
 * loaded. It runs once.
 * Rendered markup: snippets/signup-shipping-address.liquid.
 */

"use strict";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SIGNUP_SHIP_AC_STREET_ID = "shipping_street_1";
const SIGNUP_SHIP_AC_PLACE_OPTIONS = { types: ["geocode"] };
const SIGNUP_SHIP_AC_STREET_NUMBER = "street_number";
const SIGNUP_SHIP_AC_ROUTE = "route";
const SIGNUP_SHIP_AC_CITY_TYPES = ["locality", "postal_town", "sublocality"];
const SIGNUP_SHIP_AC_STATE_TYPE = "administrative_area_level_1";
const SIGNUP_SHIP_AC_ZIP_TYPE = "postal_code";
const SIGNUP_SHIP_AC_COUNTRY_TYPE = "country";

const SIGNUP_SHIP_AC_FIELD_IDS = {
  CITY: "shipping_city",
  STATE: "shipping_state",
  STATE_CODE: "shipping_state_code",
  ZIP: "shipping_zip",
  COUNTRY: "shipping_country",
  COUNTRY_CODE: "shipping_country_code",
};

let signupShippingAutocompleteReady = false;

// ---------------------------------------------------------------------------
// Field helpers
// ---------------------------------------------------------------------------

/**
 * Set a field's value and notify listeners (validation/sanitising).
 *
 * @param {string} id - Element id
 * @param {string} value
 * @param {string|null} [eventName="input"] - Event to dispatch after setting (null: none)
 */
function signupSetShippingField(id, value, eventName = "input") {
  const field = document.getElementById(id);
  if (null === field) return;

  field.value = value;
  if (eventName) field.dispatchEvent(new Event(eventName, { bubbles: true }));
}

/**
 * Pick the first address component matching one of the given types, in
 * priority order of `types`.
 *
 * @param {Array<{types: string[], long_name: string, short_name: string}>} components
 * @param {string[]} types
 * @returns {{long_name: string, short_name: string}|undefined}
 */
function signupFindAddressComponent(components, types) {
  let found;
  for (const type of types) {
    found = components.find((component) => component.types.includes(type));
    if (found) break;
  }
  return found;
}

// ---------------------------------------------------------------------------
// Place -> shipping fields
// ---------------------------------------------------------------------------

/**
 * Populate the shipping fields from a Google Place.
 *
 * @param {{address_components?: Array}} place
 */
function signupFillShippingAddress(place) {
  const components = place?.address_components;
  if (!components) return;

  const find = (types) => signupFindAddressComponent(components, types);
  const street = [find([SIGNUP_SHIP_AC_STREET_NUMBER])?.long_name, find([SIGNUP_SHIP_AC_ROUTE])?.long_name]
    .filter(Boolean)
    .join(" ");
  const city = find(SIGNUP_SHIP_AC_CITY_TYPES);
  const state = find([SIGNUP_SHIP_AC_STATE_TYPE]);
  const zip = find([SIGNUP_SHIP_AC_ZIP_TYPE]);
  const country = find([SIGNUP_SHIP_AC_COUNTRY_TYPE]);

  // No input event on the street field: Google's Autocomplete listens for
  // input and would treat it as typing, reopening the suggestion list.
  if (street) signupSetShippingField(SIGNUP_SHIP_AC_STREET_ID, street, null);
  signupSetShippingField(SIGNUP_SHIP_AC_FIELD_IDS.CITY, city?.long_name ?? "");
  signupSetShippingField(SIGNUP_SHIP_AC_FIELD_IDS.STATE, state?.long_name ?? "");
  signupSetShippingField(SIGNUP_SHIP_AC_FIELD_IDS.STATE_CODE, state?.short_name ?? "");
  signupSetShippingField(SIGNUP_SHIP_AC_FIELD_IDS.ZIP, zip?.long_name ?? "");

  const countrySelect = document.getElementById(SIGNUP_SHIP_AC_FIELD_IDS.COUNTRY);
  const match = country && countrySelect && Array.from(countrySelect.options).find((opt) => opt.text === country.long_name);
  if (match) signupSetShippingField(SIGNUP_SHIP_AC_FIELD_IDS.COUNTRY, match.value, "change");
  signupSetShippingField(SIGNUP_SHIP_AC_FIELD_IDS.COUNTRY_CODE, country?.short_name ?? "");
}

// ---------------------------------------------------------------------------
// Initialisation
// ---------------------------------------------------------------------------

/** Attach Places autocomplete to the shipping street input (runs once). */
function signupInitShippingAutocomplete() {
  if (signupShippingAutocompleteReady) return;

  const input = document.getElementById(SIGNUP_SHIP_AC_STREET_ID);
  if (!window.google?.maps?.places || null === input) return;

  const autocomplete = new window.google.maps.places.Autocomplete(input, SIGNUP_SHIP_AC_PLACE_OPTIONS);
  autocomplete.addListener("place_changed", () => signupFillShippingAddress(autocomplete.getPlace()));
  signupShippingAutocompleteReady = true;
}

// Chain onto the Maps `callback=initAutocomplete` (defined by
// company-register-v2.js, which is deferred earlier in the page).
const signupOriginalInitAutocomplete = window.initAutocomplete;
if (typeof signupOriginalInitAutocomplete === "function") {
  window.initAutocomplete = function (...args) {
    signupOriginalInitAutocomplete.apply(this, args);
    signupInitShippingAutocomplete();
  };
}

// Maps may have finished loading before this script ran.
document.addEventListener("DOMContentLoaded", signupInitShippingAutocomplete);
